/**
 * HVAC Equipment and Vent Constants
 */

// Supply Register sizes (width x height in inches)
export const SUPPLY_REGISTER_SIZES = [
  { value: '4x10', label: '4" x 10"', width: 4, height: 10 },
  { value: '4x12', label: '4" x 12"', width: 4, height: 12 },
  { value: '6x10', label: '6" x 10"', width: 6, height: 10 },
  { value: '6x12', label: '6" x 12"', width: 6, height: 12 },
  { value: '6x14', label: '6" x 14"', width: 6, height: 14 },
  { value: '10x10', label: '10" x 10"', width: 10, height: 10 },
  { value: '12x12', label: '12" x 12"', width: 12, height: 12 },
];

// Return Grille sizes (width x height in inches)
export const RETURN_GRILLE_SIZES = [
  { value: '14x14', label: '14" x 14"', width: 14, height: 14 },
  { value: '16x16', label: '16" x 16"', width: 16, height: 16 },
  { value: '14x20', label: '14" x 20"', width: 14, height: 20 },
  { value: '16x20', label: '16" x 20"', width: 16, height: 20 },
  { value: '16x25', label: '16" x 25"', width: 16, height: 25 },
  { value: '20x20', label: '20" x 20"', width: 20, height: 20 },
  { value: '20x25', label: '20" x 25"', width: 20, height: 25 },
  { value: '24x24', label: '24" x 24"', width: 24, height: 24 },
];

// Vent types
export const VENT_TYPES = {
  'supply-floor': {
    label: 'Floor Register',
    category: 'supply',
    color: '#e65100',
    strokeColor: '#bf360c',
    sizes: SUPPLY_REGISTER_SIZES,
    defaultSize: '6x12',
  },
  'supply-ceiling': {
    label: 'Ceiling Register',
    category: 'supply',
    color: '#e65100',
    strokeColor: '#bf360c',
    sizes: SUPPLY_REGISTER_SIZES,
    defaultSize: '6x12',
  },
  'supply-wall': {
    label: 'Wall Register',
    category: 'supply',
    color: '#e65100',
    strokeColor: '#bf360c',
    sizes: SUPPLY_REGISTER_SIZES,
    defaultSize: '6x12',
  },
  'return-standard': {
    label: 'Return Grille',
    category: 'return',
    color: '#1565c0',
    strokeColor: '#0d47a1',
    sizes: RETURN_GRILLE_SIZES,
    defaultSize: '16x20',
  },
  'return-filter': {
    label: 'Filter Return',
    category: 'return',
    color: '#1565c0',
    strokeColor: '#0d47a1',
    sizes: RETURN_GRILLE_SIZES,
    defaultSize: '20x25',
  },
};

// Furnace sizes
export const FURNACE_SIZES = [
  { value: '40K', label: '40,000 BTU', btu: 40000 },
  { value: '60K', label: '60,000 BTU', btu: 60000 },
  { value: '80K', label: '80,000 BTU', btu: 80000 },
  { value: '100K', label: '100,000 BTU', btu: 100000 },
  { value: '120K', label: '120,000 BTU', btu: 120000 },
];

// AC/Heat Pump sizes (tons)
export const AC_SIZES = [
  { value: '1.5', label: '1.5 Ton', tons: 1.5, btu: 18000 },
  { value: '2', label: '2 Ton', tons: 2, btu: 24000 },
  { value: '2.5', label: '2.5 Ton', tons: 2.5, btu: 30000 },
  { value: '3', label: '3 Ton', tons: 3, btu: 36000 },
  { value: '3.5', label: '3.5 Ton', tons: 3.5, btu: 42000 },
  { value: '4', label: '4 Ton', tons: 4, btu: 48000 },
  { value: '5', label: '5 Ton', tons: 5, btu: 60000 },
];

// Mini Split sizes
export const MINI_SPLIT_SIZES = [
  { value: '9K', label: '9,000 BTU', btu: 9000 },
  { value: '12K', label: '12,000 BTU', btu: 12000 },
  { value: '18K', label: '18,000 BTU', btu: 18000 },
  { value: '24K', label: '24,000 BTU', btu: 24000 },
  { value: '36K', label: '36,000 BTU', btu: 36000 },
];

// Equipment types with their properties
export const EQUIPMENT_TYPES = {
  'furnace-gas': {
    label: 'Gas Furnace',
    category: 'furnace',
    color: '#795548',
    strokeColor: '#5d4037',
    width: 24,
    height: 30,
    sizes: FURNACE_SIZES,
    defaultSize: '80K',
    symbol: 'flame',
  },
  'furnace-electric': {
    label: 'Electric Furnace',
    category: 'furnace',
    color: '#ff9800',
    strokeColor: '#f57c00',
    width: 24,
    height: 30,
    sizes: FURNACE_SIZES,
    defaultSize: '60K',
    symbol: 'bolt',
  },
  'ac-condenser': {
    label: 'AC Condenser',
    category: 'cooling',
    color: '#607d8b',
    strokeColor: '#455a64',
    width: 30,
    height: 30,
    sizes: AC_SIZES,
    defaultSize: '3',
    symbol: 'fan',
  },
  'air-handler': {
    label: 'Air Handler',
    category: 'cooling',
    color: '#78909c',
    strokeColor: '#546e7a',
    width: 24,
    height: 48,
    sizes: AC_SIZES,
    defaultSize: '3',
    symbol: 'coil',
  },
  'heat-pump': {
    label: 'Heat Pump',
    category: 'heating-cooling',
    color: '#9c27b0',
    strokeColor: '#7b1fa2',
    width: 30,
    height: 30,
    sizes: AC_SIZES,
    defaultSize: '3',
    symbol: 'hp',
  },
  'mini-split-indoor': {
    label: 'Mini Split Head',
    category: 'mini-split',
    color: '#00bcd4',
    strokeColor: '#0097a7',
    width: 36,
    height: 10,
    sizes: MINI_SPLIT_SIZES,
    defaultSize: '12K',
    symbol: 'head',
  },
  'mini-split-outdoor': {
    label: 'Mini Split Outdoor',
    category: 'mini-split',
    color: '#00838f',
    strokeColor: '#006064',
    width: 24,
    height: 24,
    sizes: MINI_SPLIT_SIZES,
    defaultSize: '12K',
    symbol: 'ms',
  },
  'thermostat': {
    label: 'Thermostat',
    category: 'control',
    color: '#4caf50',
    strokeColor: '#388e3c',
    width: 4,
    height: 4,
    sizes: [{ value: 'standard', label: 'Standard' }],
    defaultSize: 'standard',
    symbol: 't',
  },
};

// Equipment categories for grouping in UI
export const EQUIPMENT_CATEGORIES = {
  furnace: { label: 'Furnaces', types: ['furnace-gas', 'furnace-electric'] },
  cooling: { label: 'Cooling', types: ['ac-condenser', 'air-handler'] },
  'heating-cooling': { label: 'Heat Pumps', types: ['heat-pump'] },
  'mini-split': { label: 'Mini Splits', types: ['mini-split-indoor', 'mini-split-outdoor'] },
  control: { label: 'Controls', types: ['thermostat'] },
};

// Default vent properties
export const DEFAULT_VENT = {
  type: 'supply-floor',
  size: '6x12',
  cfm: null,
  rotation: 0,
};

// Default equipment properties
export const DEFAULT_EQUIPMENT = {
  type: 'furnace-gas',
  size: '80K',
  rotation: 0,
};
